import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux-toolkit/store';
import moment from 'moment';
import { getData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import MeFWContainer from './MeFWContainer';
import MeJWContainer from './MeJWContainer';
import MeNCAContainer from './MeNCAContainer';

export interface AgendaCounts {
    jw: number;
    doctorReported: number;
    doctorTotal: number;
    pharmacyReported: number;
    pharmacyTotal: number;
    stockistReported: number;
    stockistTotal: number;
    nca: number;
}

interface MeAgendaContainerProps {
    defaultTab?: 'JW' | 'Doctor' | 'Pharmacy' | 'Stockist' | 'NCA';
}

export default function MeAgendaContainer({ defaultTab }: MeAgendaContainerProps = {}) {
    const location = useLocation();
    const reduxSelectedDate = useSelector((state: RootState) => state.position.selectedDate);
    const initialDate = location.state?.date || reduxSelectedDate || localStorage.getItem("selectedDate") || moment().format('YYYY-MM-DD');
    const [selectedDate, setSelectedDate] = useState<string>(initialDate);

    const initialTab = (location.state?.tab || location.state?.filter || defaultTab || 'Doctor') as 'JW' | 'Doctor' | 'Pharmacy' | 'Stockist' | 'NCA';
    const [currentTab, setCurrentTab] = useState<'JW' | 'Doctor' | 'Pharmacy' | 'Stockist' | 'NCA'>(initialTab);

    const [counts, setCounts] = useState<AgendaCounts>({
        jw: 0,
        doctorReported: 0,
        doctorTotal: 0,
        pharmacyReported: 0,
        pharmacyTotal: 0,
        stockistReported: 0,
        stockistTotal: 0,
        nca: 0
    });

    useEffect(() => {
        const activeDate = location.state?.date || reduxSelectedDate || localStorage.getItem("selectedDate") || moment().format('YYYY-MM-DD');
        if (activeDate !== selectedDate) {
            setSelectedDate(activeDate);
        }
    }, [reduxSelectedDate, location.state?.date]);

    useEffect(() => {
        if (location.state?.tab) {
            setCurrentTab(location.state.tab);
        } else if (location.state?.filter) {
            setCurrentTab(location.state.filter);
        }
    }, [location.state]);

    const refreshAgendaCounts = async () => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || !selectedDate) return;

        const formattedDateParam = moment(selectedDate).format('YYYY-MM-DD');

        try {
            const jwUrl = `${serverUrl}${API.getJointWork}`;
            const jwRes = await getData(jwUrl, true, { date: formattedDateParam });
            let jwCount = 0;
            if (jwRes.status === 200 || jwRes.data?.statusCode === 200) {
                const payload = jwRes.data?.data || jwRes.data;
                const items = Array.isArray(payload?.dailycalls) ? payload.dailycalls : (Array.isArray(payload) ? payload : (Array.isArray(payload?.items) ? payload.items : []));
                jwCount = items.length;
            }

            const fwUrl = `${serverUrl}${API.getFieldWork}`;
            const fwRes = await getData(fwUrl, true, { date: formattedDateParam });
            let docRep = 0, docTotal = 0, pharmRep = 0, pharmTotal = 0, stockRep = 0, stockTotal = 0;
            if (fwRes.status === 200 || fwRes.data?.statusCode === 200) {
                const d = fwRes.data;
                const payload = d?.data || d;
                const dailycallsRaw = Array.isArray(payload?.dailycalls) ? payload.dailycalls : [];
                const dayplanRaw = Array.isArray(payload?.dayplan) ? payload.dayplan : [];

                const isJwItem = (item: any) => {
                    const isjwVal = item.Isjw ?? item.isjw;
                    return isjwVal === true || isjwVal === 'true' || isjwVal === 1 || isjwVal === '1';
                };

                const dailycallsFiltered = dailycallsRaw;
                const dayplanFiltered = dayplanRaw
                    .filter((item: any) => `${item.DcrStatus ?? item.dcr_status ?? item.status ?? ''}`.toLowerCase() !== 'completed');

                let list: any[] = [];
                if (dayplanRaw.length > 0 || dailycallsRaw.length > 0) {
                    list = [...dayplanFiltered, ...dailycallsFiltered];
                } else if (Array.isArray(payload)) {
                    list = payload;
                } else if (Array.isArray(payload?.items)) {
                    list = payload.items;
                }

                const getItemCategory = (item: any): 'Doctor' | 'Pharmacy' | 'Stockist' => {
                    const rawType = (
                        item.outlet?.OutlettypeName ||
                        item.outlet?.outlet_type_name ||
                        item.outlet?.outlet_type ||
                        item.outlet?.Classification ||
                        item.outlet_type ||
                        item.OutletType ||
                        item.OutlettypeName ||
                        item.classification ||
                        ''
                    ).toLowerCase();

                    if (rawType.includes('pharm') || rawType.includes('chem') || rawType.includes('drug') || rawType.includes('retail')) {
                        return 'Pharmacy';
                    }
                    if (rawType.includes('stock') || rawType.includes('dist') || rawType.includes('wholesal')) {
                        return 'Stockist';
                    }
                    return 'Doctor';
                };

                const docItems = list.filter(it => getItemCategory(it) === 'Doctor');
                const pharmItems = list.filter(it => getItemCategory(it) === 'Pharmacy');
                const stockItems = list.filter(it => getItemCategory(it) === 'Stockist');

                docRep = docItems.filter(it => `${it.dcr_status || it.DcrStatus || ''}`.toLowerCase() === 'reported').length;
                pharmRep = pharmItems.filter(it => `${it.dcr_status || it.DcrStatus || ''}`.toLowerCase() === 'reported').length;
                stockRep = stockItems.filter(it => `${it.dcr_status || it.DcrStatus || ''}`.toLowerCase() === 'reported').length;

                docTotal = docItems.length;
                pharmTotal = pharmItems.length;
                stockTotal = stockItems.length;
            }

            const ncaUrl = `${serverUrl}${API.getNca}`;
            const ncaRes = await getData(ncaUrl, true, { date: formattedDateParam });
            let ncaCount = 0;
            if (ncaRes.status === 200 || ncaRes.data?.statusCode === 200) {
                const payload = ncaRes.data?.data || ncaRes.data;
                const dailycallsRaw = Array.isArray(payload?.dailycalls) ? payload.dailycalls : [];
                const dayplanRaw = Array.isArray(payload?.dayplan) ? payload.dayplan : [];

                const dayplanFiltered = dayplanRaw.filter((item: any) => `${item.DcrStatus ?? item.dcr_status ?? item.status ?? ''}`.toLowerCase() !== 'completed');

                if (dayplanRaw.length > 0 || dailycallsRaw.length > 0) {
                    ncaCount = dayplanFiltered.length + dailycallsRaw.length;
                } else if (Array.isArray(payload)) {
                    ncaCount = payload.length;
                } else if (Array.isArray(payload?.items)) {
                    ncaCount = payload.items.length;
                }
            }

            setCounts({
                jw: jwCount,
                doctorReported: docRep,
                doctorTotal: docTotal,
                pharmacyReported: pharmRep,
                pharmacyTotal: pharmTotal,
                stockistReported: stockRep,
                stockistTotal: stockTotal,
                nca: ncaCount
            });
        } catch (err) {
            console.error("Error refreshing agenda counts:", err);
        }
    };

    // Fetch counts for all agenda tabs for the selected date
    useEffect(() => {
        refreshAgendaCounts();
    }, [selectedDate]);

    if (currentTab === 'JW') {
        return <MeJWContainer onTabChange={setCurrentTab} agendaCounts={counts} onRefreshCounts={refreshAgendaCounts} />;
    }

    if (currentTab === 'NCA') {
        return <MeNCAContainer onTabChange={setCurrentTab} agendaCounts={counts} onRefreshCounts={refreshAgendaCounts} />;
    }

    return (
        <MeFWContainer
            initialCategory={currentTab === 'Pharmacy' || currentTab === 'Stockist' ? currentTab : 'Doctor'}
            onTabChange={setCurrentTab}
            agendaCounts={counts}
            onRefreshCounts={refreshAgendaCounts}
        />
    );
}
